import _ from "lodash";
import React, { useEffect, useState } from "react";
import { Segment, Button, Form, Icon, Message } from "semantic-ui-react";
import { useDispatch } from "react-redux";
import { initialize, Field, reduxForm, FieldArray } from "redux-form";
import { ApiRequest } from "../../firebase/server";
import { createRetailer } from "../../firebase";
import {
  TextInput,
  SelectInput,
  CheckboxInput,
} from "../../templates/utils/form-fields";

const FieldList = ({ fields }) => {
  return (
    <>
      <Form.Field>
        <Icon
          size="large"
          color="teal"
          onClick={() => fields.push({})}
          link
          name="plus"
          className="pushdown"
        />
      </Form.Field>
      {fields.map((template, index) => (
        <Form.Group key={index} className="clearboth">
          <Form.Field>
            <label>Name</label>
            <Field
              type="text"
              name={`${template}.name`}
              component={TextInput}
              value={template.name}
            />
          </Form.Field>
          <Form.Field>
            <label>Description</label>
            <Field
              type="text"
              name={`${template}.description`}
              component={TextInput}
              value={template.description}
            />
          </Form.Field>
          <Form.Field>
            <label>ct Variable</label>
            <Field
              type="text"
              name={`${template}.variable`}
              component={TextInput}
              value={template.variable}
            />
          </Form.Field>
          <Form.Field>
            <Field
              name={`${template}.type`}
              label="Type"
              type="text"
              component={SelectInput}
              options={[
                { text: "String", value: "String", key: "string" },
                { text: "File", value: "File", key: "file" },
                {
                  text: "ExternalFile",
                  value: "ExternalFile",
                  key: "externalfile",
                },
                { text: "Array", value: "Array", key: "array" },
                { text: "Number", value: "Number", key: "number" },
                { text: "Object", value: "Object", key: "object" },
              ]}
              multiple={false}
            />
          </Form.Field>
          <Form.Field>
            <label>Required</label>
            <Field
              name={`${template}.required`}
              label=" "
              type="checkbox"
              value={template.required}
              component={CheckboxInput}
            />
          </Form.Field>
          <Form.Field>
            <label>&nbsp;</label>
            <Icon
              onClick={() => fields.remove(index)}
              link
              size="large"
              color="red"
              name="trash"
            />
          </Form.Field>
        </Form.Group>
      ))}
    </>
  );
};

const ContentList = ({ fields }) => {
  return (
    <>
      <Form.Field>
        <Icon
          size="large"
          color="teal"
          onClick={() => fields.push({})}
          link
          name="plus"
          className="pushdown"
        />
      </Form.Field>
      {fields.map((field, index) => (
        <Form.Group key={index} className="clearboth">
          <Form.Field>
            <Field
              name={`${field}.type`}
              label="Type"
              type="text"
              component={SelectInput}
              options={[
                { text: "HTML", value: "HTML", key: "html" },
                { text: "CSS", value: "CSS", key: "css" },
                { text: "RAW", value: "RAW", key: "raw" },
                { text: "JavaScript", value: "JavaScript", key: "javascript" },
                {
                  text: "JavaScriptExternal",
                  value: "JavaScriptExternal",
                  key: "javascriptexternal",
                },
              ]}
              multiple={false}
            />
          </Form.Field>
          <Form.Field width={12}>
            <label>Body</label>
            <Field
              name={`${field}.body`}
              component="textarea"
              rows="12"
              value={field.body}
            />
          </Form.Field>
          <Form.Field>
            <label>&nbsp;</label>
            <Icon
              onClick={() => fields.remove(index)}
              link
              size="large"
              color="red"
              name="trash"
            />
          </Form.Field>
        </Form.Group>
      ))}
    </>
  );
};

let KevelStandard = ({ retailer, id, handleSubmit, pristine, submitting }) => {
  const dispatch = useDispatch();

  const [showLoading, setShowLoading] = useState(false);
  const [showMessage, setShowMessage] = useState(null);

  const handleSubmitForm = (values) => {
    const update = { ...retailer };
    update.templates[id].fields = values.fields;
    update.templates[id].contents = values.contents;

    //create or update Kevel template
    const template = update.templates[id];
    let call = "createTemplate";
    let obj = {};

    if (template.id) {
      call = "updateTemplate";
      obj = {
        id: template.id,
        updates: [
          {
            path: [],
            op: "Update",
            value: _.omit(template, ["id", "type", "service", "displayName"]),
          },
        ],
      };
    } else {
      obj = _.omit(template, ["type", "service", "displayName"]);
    }

    //call bannermatic api and update template with id from kevel
    //start loading overlay
    setShowLoading(true);

    ApiRequest({
      retailer: retailer.name,
      template: obj,
      call,
      service: retailer.service,
    })
      .then((data) => {
        console.log("createTemplate", data);
        update.templates[id].id = data.result.id || data.result.Id;
        //update retailer in firestore
        createRetailer(update)
          .then(() => {
            //end loading overlay
            setShowLoading(false);
            const createdOrUpdated =
              call === "createTemplate" ? "created" : "updated";
            setShowMessage({
              color: "green",
              content: `Kevel template ${obj.name} has been ${createdOrUpdated}.`,
            });
          })
          .catch((error) => {
            //show error message
            console.log("createRetailer error", error.message);
            setShowLoading(false);
            setShowMessage({
              color: "red",
              content: error.message,
            });
          });
      })
      .catch((error) => {
        //show error message
        setShowLoading(false);
        setShowMessage({
          color: "red",
          content: "Failed to upload template: invalid content",
        });
      });
  };

  useEffect(() => {
    dispatch(initialize("kevelStandardForm", retailer.templates[id]));
  }, [dispatch, retailer, id]);

  return (
    <Segment loading={showLoading}>
      <Form onSubmit={handleSubmit(handleSubmitForm)}>
        <h3>Fields</h3>
        <FieldArray name="fields" component={FieldList} />
        <h3>Contents</h3>
        <FieldArray name="contents" component={ContentList} />
        <Button
          className="clearboth"
          positive
          size="tiny"
          type="submit"
          disabled={submitting || pristine}
        >
          Submit
        </Button>
      </Form>
      {showMessage && (
        <Message
          color={showMessage.color}
          content={showMessage.content}
          onDismiss={() => setShowMessage(null)}
        />
      )}
    </Segment>
  );
};

KevelStandard = reduxForm({
  form: "kevelStandardForm",
})(KevelStandard);

export default KevelStandard;
